import React from 'react';
import { Chip, ChipProps } from '@mui/material';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { PassBookingLink, PassBookingState } from '../../models/Pass';
import { passMetaChipSx } from './passDetailLayout';

const STATE_COLOR: Record<PassBookingState, ChipProps['color']> = {
  RESERVED: 'info',
  CONSUMED: 'success',
  RELEASED: 'default',
};

/** Resolves an absolute or same-origin `passDetailUrl` to a router-relative path, so we can use `Link` instead of a full page reload. */
function toRelativePath(url: string): string {
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return url;
  }
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
}

interface PassBookingChipProps {
  pass: PassBookingLink;
  size?: 'small' | 'medium';
}

/** Compact chip linking to the pass detail page, shown on bookings paid with a pass. */
const PassBookingChip: React.FC<PassBookingChipProps> = ({ pass, size = 'small' }) => {
  const { t } = useTranslation();

  const label = t(`passes.bookingState.${pass.passBookingState}`, pass.passBookingState);
  const color = STATE_COLOR[pass.passBookingState] || 'default';
  const to = toRelativePath(pass.passDetailUrl);

  return (
    <Chip
      component={RouterLink}
      to={to}
      clickable
      variant="outlined"
      icon={<ConfirmationNumberIcon />}
      label={label}
      color={color}
      size={size}
      onClick={(e) => e.stopPropagation()}
      sx={
        size === 'small'
          ? passMetaChipSx
          : {
              fontWeight: 500,
              '& .MuiChip-label': { px: 0.75, fontSize: '0.8125rem', lineHeight: 1.2 },
              '& .MuiChip-icon': { fontSize: 18 },
            }
      }
    />
  );
};

export default PassBookingChip;
